// Config
import deliveryConstants, { undeliverableReasonConstants } from '@constants/deliveryConstants';
import { isEmptyObject } from '@utils/object';

export default function getUndeliverableItems(deliveries = [], items = []) {
  const allUndeliverableItems = [];

  const restrictedItemsInDeliveries = deliveries
    .map((d) =>
      d.items
        ? d.items.filter(
            (item) =>
              item.undeliverableReasons?.includes(undeliverableReasonConstants.RESTRICTED) ||
              item.undeliverableReasons?.includes(undeliverableReasonConstants.NI_RESTRICTED),
          )
        : [],
    )
    .reduce((acc, items) => acc.concat(items), []);

  if (!isEmptyObject(restrictedItemsInDeliveries)) {
    const restrictedItemIds = new Set(restrictedItemsInDeliveries.map((item) => item.id));

    items.forEach((item) => {
      if (restrictedItemIds.has(item.id)) {
        // Find the corresponding delivery item to get the undeliverable reasons
        const deliveryItem = restrictedItemsInDeliveries.find((dItem) => dItem.id === item.id);
        allUndeliverableItems.push({
          ...item,
          undeliverableReasons: deliveryItem?.undeliverableReasons || [],
        });
      }

      if (item.childItems) {
        item.childItems.forEach((childItem) => {
          if (restrictedItemIds.has(childItem.id)) {
            // Find the corresponding delivery item to get the undeliverable reasons
            const deliveryItem = restrictedItemsInDeliveries.find((dItem) => dItem.id === childItem.id);
            allUndeliverableItems.push({
              ...childItem,
              undeliverableReasons: deliveryItem?.undeliverableReasons || [],
            });
          }
        });
      }
    });
  }

  // Handle other undeliverable deliveries
  const undeliverableDeliveries = deliveries.filter((d) => d.type === deliveryConstants.UNDELIVERABLE);

  if (!isEmptyObject(undeliverableDeliveries)) {
    const otherUndeliverableItems = undeliverableDeliveries
      .map((d) =>
        items
          .filter((item) => d.items.map((delivery) => delivery.id).includes(item.id))
          .map((preOrderItem) => {
            const undeliverableItems = d.items?.filter((deliverableItem) => deliverableItem.id === preOrderItem.id);
            const undeliverableReasons = (undeliverableItems && undeliverableItems[0]?.undeliverableReasons) || [];
            return {
              ...preOrderItem,
              ...(undeliverableReasons?.length && { undeliverableReasons }),
              ...(d.streetDate && { streetDate: d.streetDate }),
            };
          }),
      )
      .flat();

    // Merge with restricted items, avoiding duplicates
    const existingItemIds = new Set(allUndeliverableItems.map((item) => item.id));
    otherUndeliverableItems.forEach((item) => {
      if (!existingItemIds.has(item.id)) {
        allUndeliverableItems.push(item);
      } else {
        // If item already exists, merge the undeliverable reasons
        const existingItem = allUndeliverableItems.find((existing) => existing.id === item.id);
        if (existingItem && item.undeliverableReasons) {
          const combinedReasons = [
            ...new Set([...(existingItem.undeliverableReasons || []), ...item.undeliverableReasons]),
          ];
          existingItem.undeliverableReasons = combinedReasons;

          // Also merge streetDate if it exists
          if (item.streetDate && !existingItem.streetDate) {
            existingItem.streetDate = item.streetDate;
          }
        }
      }
    });
  }

  return allUndeliverableItems.length > 0 ? allUndeliverableItems : null;
}
